import * as React from "react";
import { useHistory } from "react-router";
import { useIntl } from "react-intl";
import { Grid, Loader, Message, Button, Header } from "semantic-ui-react";
import { toast } from "sonner";

import { SideBar } from "@/components/SideBar";
import { TitleSection } from "@/components/TitleSection";
import { AutoSubscriptions } from "@/components/PlayersSubscriptions";
import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { Dialog, Spinner } from "@/components/ui";
import {
  Campaign,
  PromptCampaign,
  SelectedPrompts,
  CampaignRequest,
  CampaignStep,
  WebComponents,
  CampaignAccess,
} from "@/interfaces";
import { CampaignServices, CoachService, GenericAccess } from "@/services";
import { useAccountSettings, URLS, useSocialCoachSession, useInterval, STORAGE_KEYS } from "@/utils";

import { BasicInfoStep } from "./components/BasicInfo";
import { PromptsStep } from "./components/Prompts";

import { CampaignPageType, descriptors } from "./descriptors";

import { CampaignReducer } from "./reducers";
import { actions } from "./reducers/actions";

import { WhiteContainer, ContentGridSection } from "@/styling/baseStyle";
import "./styles.scss";

import { CampaignReview } from "./components/Review";
import { EligibilityEditor } from "@/containers/Coach/EligibilityEditor";
import { useMemo, useState } from "react";
import { EligibilityCriterion } from "@/services/bedrock";

const CampaignFC: React.FC = () => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { coachId } = useSocialCoachSession();
  const basicInfoRef = React.useRef<HTMLFormElement>(null);
  const reviewRef = React.useRef<HTMLFormElement>(null);
  const [allAccess, setAllAccess] = useState<GenericAccess>();
  const [campaignAccess, setCampaignAccess] = useState<CampaignAccess>();

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.CAMPAIGNS);

  const [campaign, setCampaign] = React.useState<Campaign | undefined>(
    localStorage.getItem(STORAGE_KEYS.COACH_CAMPAIGN)
      ? JSON.parse(localStorage.getItem(STORAGE_KEYS.COACH_CAMPAIGN)!)
      : undefined
  );

  const [submitError, setSubmitError] = React.useState<string | undefined>(undefined);
  const [stepError, setStepError] = React.useState<string | undefined>(undefined);

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [savingCampaignId, setSavingCampaignId] = React.useState<string | undefined>(undefined);

  const [nextButtonDisabled, setNextButtonDisabled] = React.useState<boolean>(true);

  const [state, dispatch] = React.useReducer(CampaignReducer, {
    currentStep: Number(localStorage.getItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP)) || 0,
    openConfirmationModal: false,
    prepareConfirmationModal: false,
  });

  const closeAndClean = React.useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP);
    localStorage.removeItem(STORAGE_KEYS.COACH_CAMPAIGN);
    dispatch({ name: actions.CANCEL });
  }, []);

  const pollStatus = React.useCallback(async () => {
    if (!savingCampaignId) return;
    try {
      const statusResponse = await CampaignServices.getCampaignStatus(savingCampaignId);
      if (!statusResponse.isProcessing) {
        setSavingCampaignId(undefined);
        toast.success("Campaign created successfully!");
        closeAndClean();
      }
    } catch (error) {
      console.error("Error polling campaign status:", error);
      setSavingCampaignId(undefined);
      closeAndClean();
    }
  }, [savingCampaignId, closeAndClean]);

  useInterval(pollStatus, savingCampaignId ? 5000 : undefined);

  React.useEffect(() => {
    if (savingCampaignId) {
      pollStatus();
    }
  }, [savingCampaignId, pollStatus]);

  const stepsValidations: { [key: string]: CampaignStep } = {
    0: {
      validations: () =>
        new Promise((_, reject) => {
          if (basicInfoRef.current) {
            basicInfoRef!.current!.handleSubmit();
          }
          reject();
        }),
    },
    1: {
      validations: () =>
        new Promise((resolve, reject) => {
          if (campaign?.campaignPrompts && campaign?.campaignPrompts.length > 0) {
            resolve("");
          } else {
            reject(
              formatMessage({
                ...descriptors[CampaignPageType.promptStepError],
              })
            );
          }
        }),
    },
    2: {
      validations: () =>
        new Promise((_, reject) => {
          if (reviewRef.current) {
            reviewRef!.current!.handleSubmit();
          }
          reject();
        }),
    },
    3: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
    4: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
  };

  const currentStep = Number(state.currentStep);
  const noFirstStep = currentStep > 0;
  const finalStep = currentStep >= 4;

  const saveButtonTitle: string = formatMessage({
    ...descriptors[CampaignPageType[currentStep < 4 ? "nextBtn" : "publishBtn"]],
  });

  const reloadAccess = async (newCriteria: EligibilityCriterion[] = []) => {
    const newAccess = await CoachService.getAccessPreview(coachId?.toString()!, "CAMPAIGNS", newCriteria);
    setCampaignAccess(c => {
      return { ...c, ...newAccess } as CampaignAccess;
    });
    return newAccess;
  };

  React.useEffect(() => {
    reloadAccess().then(setAllAccess).catch(console.error);
  }, [coachId]);

  React.useEffect(() => {
    setStepError(undefined);

    if (currentStep < 0) {
      history.push(URLS.coach.campaigns);
    } else {
      localStorage.setItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP, `${currentStep}`);
    }
  }, [state]);

  React.useEffect(() => {
    if (campaign) {
      localStorage.setItem(STORAGE_KEYS.COACH_CAMPAIGN, JSON.stringify(campaign));
    }

    if (state.prepareConfirmationModal) {
      saveCampaign("UNPUBLISHED");
    }
  }, [campaign]);

  const saveCampaign = async (status: string = "ACTIVE"): Promise<any> => {
    try {
      if (campaign) {
        const {
          name,
          isAutoPost,
          autoEnroll,
          campaignPrompts,
          deliveryMode,
          randomScheduleDays,
          randomScheduleDeliveryTimePst,
          categoryIds,
          startDate,
          endDate,
        } = campaign;

        const isScheduledDelivery = deliveryMode === "SPECIFIC_DATE";
        const prompts = campaignPrompts.map((cPrompt: PromptCampaign, index) => {
          return {
            promptId: cPrompt.prompt!.promptId,
            socialNetworks: cPrompt.socialNetworks,
            sortOrder: index,
            deliveryDatetimeUtc: isScheduledDelivery ? cPrompt.deliveryDatetimeUtc : undefined,
          } as PromptCampaign;
        });

        let campaignRequest: CampaignRequest = {
          name,
          isAutoPost,
          autoEnroll,
          campaignPrompts: prompts,
          categoryIds,
          deliveryMode,
          status,
          campaignAccess,
        };

        if (!isScheduledDelivery) {
          campaignRequest = {
            ...campaignRequest,
            randomScheduleDays,
            startDate,
            endDate,
          };
          if (randomScheduleDeliveryTimePst) {
            campaignRequest = {
              ...campaignRequest,
              randomScheduleDeliveryTimePst,
            };
          }
        }

        setIsSaving(true);
        CampaignServices.create(campaignRequest, coachId!)
          .then(response => {
            setSavingCampaignId(response.campaignId);
            setSubmitError(undefined);
          })
          .catch(e => {
            setSubmitError(
              e.length < 150
                ? e
                : formatMessage({
                    ...descriptors[CampaignPageType.savingError],
                  })
            );
            dispatch({ name: actions.CLEAN_CONFIRMATION_MODAL });
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    } catch (e: any) {
      setSubmitError(e);
    }
  };

  const onCloseModal = () => {
    dispatch({ name: actions.CLOSE_CONFIRMATION_MODAL });
  };

  const onCancelAction = () => {
    dispatch({ name: actions.OPEN_CONFIRMATION_MODAL });
  };

  const onBackAction = () => {
    setStepError(undefined);
    setSubmitError(undefined);
    dispatch({ name: actions.PREVIOUS_STEP });
  };

  const handleSubmitFormPromise = (): Promise<any> => {
    return stepsValidations[currentStep].validations();
  };

  const prepareForSave = () => {
    if (currentStep <= 4) {
      handleSubmitFormPromise()
        .then(() => {
          saveCampaign("UNPUBLISHED");
        })
        .catch(e => {
          if (e && e !== "") {
            setSubmitError(e);
            setStepError(undefined);
          }
        });
    }
  };

  const onNextButtonAction = () => {
    setSubmitError(undefined);
    if (currentStep <= 4) {
      handleSubmitFormPromise()
        .then(() => {
          nextStep();
        })
        .catch(e => {
          if (e && e !== "") {
            setStepError(e);
          }
        });
    }
  };

  const nextStep = (step: number = currentStep + 1) => {
    localStorage.setItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP, `${step}`);
    if (currentStep === 4) {
      saveCampaign();
    }
    dispatch({ name: actions.NEXT_STEP });
  };

  const basicInfoSuccessHandler = (updatedCampaign: Campaign) => {
    setCampaign(updatedCampaign);
    // HERE
    nextStep(1);
  };

  const promptsSuccessHandler = (selectedPrompts: SelectedPrompts) => {
    if (Object.keys(selectedPrompts).length > 0) {
      setStepError(undefined);
    }

    const campaignPrompts = Object.keys(selectedPrompts).map(promptId => {
      const isPrevious = campaign?.campaignPrompts.filter(c => `${c.prompt!.promptId}` === `${promptId}`);
      const customP = selectedPrompts[promptId].prompt;
      return {
        promptId: promptId!,
        title: selectedPrompts[promptId].title,
        post: selectedPrompts[promptId].post,
        prompt: {
          ...selectedPrompts[promptId].prompt,
          deliveryDatetimeUtc: isPrevious && isPrevious!.length > 0 ? isPrevious[0].deliveryDatetimeUtc : undefined,
        },
        deliveryDatetimeUtc: isPrevious && isPrevious!.length > 0 ? isPrevious[0].deliveryDatetimeUtc : undefined,
        socialNetworks: campaign!.socialNetworks
          ? Object.keys(campaign!.socialNetworks).filter(sn => {
              return (
                campaign!.socialNetworks &&
                campaign!.socialNetworks[sn] &&
                ((customP!.post?.media[0] && customP!.post?.media[0].uri) ||
                  (sn !== "IG" && sn !== "TT" && sn !== "IG_STORY"))
              );
            })
          : [],
      } as PromptCampaign;
    });

    setCampaign(c => {
      return { ...c!, campaignPrompts };
    });
  };

  const reviewSuccessHandler = (prompts: PromptCampaign[]) => {
    setCampaign({
      ...campaign!,
      campaignPrompts: prompts,
    });

    if (!state.prepareConfirmationModal) {
      nextStep(3);
    }
  };

  const subscriptionOnChangeHandler = (playersId: number[]) => {
    setCampaignAccess(c => {
      return {
        ...c,
        subscribedPlayerIds: playersId,
        pendingPlayerIds: (c?.pendingPlayerIds || []).filter(id => playersId.includes(id)),
      } as CampaignAccess;
    });
  };

  const campaignsPromptsConverted = (): SelectedPrompts => {
    if (campaign?.campaignPrompts) {
      let selectedPrompts: SelectedPrompts = {};
      campaign?.campaignPrompts.map(cPrompt => {
        const { title, post, categories } = cPrompt.prompt!;
        selectedPrompts = {
          ...selectedPrompts,
          [cPrompt.promptId]: {
            title,
            post,
            prompt: cPrompt.prompt,
            categoryIds: categories ? categories!.map(category => category!.categoryId!) : [],
          },
        };
      });
      return selectedPrompts;
    }

    return {};
  };

  const selectedPlayerIds: number[] = useMemo(() => {
    if (campaignAccess) {
      const l1 = campaignAccess.subscribedPlayerIds || [];
      const l2 = campaignAccess.pendingPlayerIds || [];
      const merged = l1.concat(...l2);
      return Array.from(new Set(merged));
    } else {
      return [];
    }
  }, [campaignAccess]);

  const stepSection = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep
            basicInfoSubmitRef={basicInfoRef}
            campaign={campaign}
            onValidationHandler={withErrors => {
              setNextButtonDisabled(withErrors);
            }}
            onSuccessHandler={basicInfoSuccessHandler}
          />
        );
      case 1:
        return (
          <PromptsStep
            coachId={coachId?.toString()!}
            campaignPrompts={campaignsPromptsConverted()}
            onChangeHandler={promptsSuccessHandler}
          />
        );
      case 2:
        return (
          <CampaignReview
            submitRef={reviewRef}
            campaignDeliveryMode={campaign!.deliveryMode}
            campaignPrompts={campaign!.campaignPrompts}
            isAutoPost={campaign!.isAutoPost}
            onSuccessHandler={reviewSuccessHandler}
            onErrorHandler={onCloseModal}
            goBackHandler={onBackAction}
          />
        );
      case 3:
        return (
          <>
            <Header as="h4">Campaign Eligibility</Header>
            {allAccess && campaignAccess && (
              <EligibilityEditor
                isNew={!campaign?.campaignId}
                originalAccess={allAccess}
                currentAccess={campaignAccess}
                accessType={"CAMPAIGNS"}
                updateHandler={async newCriteria => {
                  setCampaignAccess(c => {
                    return { ...c, eligibilityCriteria: newCriteria } as CampaignAccess;
                  });
                  const newAccess = await reloadAccess(newCriteria);
                  setCampaignAccess(c => {
                    return { ...c, ...newAccess } as CampaignAccess;
                  });
                }}
              />
            )}
          </>
        );
      default:
        return (
          <>
            <Header as="h4">Campaign Auto-Enrollment</Header>
            <AutoSubscriptions
              disabled={!withWritePermission}
              eligiblePlayerIds={campaignAccess?.eligiblePlayerIds || []}
              isInclude={true}
              selectedPlayers={selectedPlayerIds}
              onChange={subscriptionOnChangeHandler}
              autoEnroll={campaign?.autoEnroll}
              onAutoEnrollChange={autoEnroll => {
                setCampaign(c => ({ ...c!, autoEnroll }));
              }}
              isAutoPost={campaign?.isAutoPost}
            />
          </>
        );
    }
  };

  const customLinkSection = () => {
    return (
      <Grid.Column width={noFirstStep ? (finalStep ? 9 : 11) : 4} floated="right" className="noMarginTop">
        <Button className={"secondary buttonWithLateralPadding"} onClick={onCancelAction}>
          {formatMessage({ ...descriptors[CampaignPageType.cancelBtn] })}
        </Button>
        {noFirstStep && (
          <Button
            data-elm-id={`campaignBackBtn`}
            className={"secondary buttonWithLateralPadding"}
            onClick={onBackAction}
          >
            {formatMessage({ ...descriptors[CampaignPageType.backBtn] })}
          </Button>
        )}
      </Grid.Column>
    );
  };

  const confirmationModal = () => {
    return (
      <ConfirmationModal
        openConfirmationModal={state.openConfirmationModal}
        onClose={onCloseModal}
        okHandler={() => {
          dispatch({ name: actions.ACCEPT_CONFIRMATION_MODAL });
          prepareForSave();
        }}
        rejectHandler={closeAndClean}
      />
    );
  };

  const processingDialog = () => {
    const isPolling = !!savingCampaignId;
    return (
      <Dialog
        open={isSaving || isPolling}
        onOpenChange={open => {
          if (!open) {
            setIsSaving(false);
            setSavingCampaignId(undefined);
            closeAndClean();
          }
        }}
      >
        <Dialog.Header>
          <Dialog.Title>{isPolling ? "Finalizing Campaign Setup" : "Saving Campaign"}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
            <Spinner className="w-8 h-8 text-primary" />
            <div className="space-y-2">
              <p className="text-base font-medium">
                {isPolling ? "Syncing with your team..." : "Securing your changes..."}
              </p>
              <p className="text-sm text-muted-foreground italic">
                {isPolling
                  ? "This may take a moment. You can close this window and continue working while we finish up."
                  : "Hang tight, we're almost there."}
              </p>
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            className="secondary"
            onClick={() => {
              setIsSaving(false);
              setSavingCampaignId(undefined);
              closeAndClean();
            }}
          >
            {isPolling ? "Run in background" : "Cancel"}
          </Button>
        </Dialog.Footer>
      </Dialog>
    );
  };

  return (
    <WhiteContainer>
      <SideBar history={history} />

      <ContentGridSection columns={1}>
        <TitleSection
          title={formatMessage({ ...descriptors[CampaignPageType.title] })}
          customLinkSection={customLinkSection()}
          customNextLabel={saveButtonTitle}
          disableButtons={nextButtonDisabled}
          titleSize={finalStep ? 6 : 8}
          buttonsSize={finalStep ? 10 : 8}
          nextButtonSize={noFirstStep && !finalStep ? 5 : 7}
          handleNextAction={onNextButtonAction}
          showLoading={false}
          showLinkOption={withWritePermission}
          showNextOption={withWritePermission}
        />

        {(stepError || submitError) && (
          <Grid.Row>
            <Message className={"leftMargin"} error list={[submitError, stepError]} />
          </Grid.Row>
        )}
        <Grid.Row className={"campaignFormSection"}>
          <Grid.Column width={16} className={"formContainer"}>
            {isSubmitting ? <Loader active size={"large"} /> : stepSection()}
          </Grid.Column>
        </Grid.Row>

        {confirmationModal()}
        {processingDialog()}
      </ContentGridSection>
    </WhiteContainer>
  );
};

export const CampaignPage = CampaignFC;
